// src/components/TrustSection.jsx
import React from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { motion } from 'framer-motion';
import { Sparkles, Lock, Zap, Eye } from 'lucide-react'; // replace icons as needed

const trustItems = [
  {
  icon: <Sparkles className="w-5 h-5 text-pink-400" />,
  title: 'Game Treasury (Public Wallet)',
  summary: 'All rewards come from a public, viewable TRUST wallet — not a hidden dev stash.',
  body: (
    <>
      <p>
        This treasury is fully on-chain and transparent. While it currently uses a standard Phantom wallet for
        simplicity and fast rewards distribution, we plan to migrate to a multi-sig setup (via Squads) as volume grows.
      </p>
      <ul className="list-disc pl-6 mt-2">
        <li>Funds are visible to everyone on-chain</li>
        <li>Used only for in-game rewards</li>
        <li>No rug — no hidden dev wallet</li>
      </ul>
      <p className="mt-3">
        <strong>View the wallet:</strong>{' '}
        <a
          href="https://bscscan.com/address/0xb692e9099a6b275cfc990c994f0d62cca2284444"
          target="_blank"
          rel="noopener noreferrer"
          className="text-pink-400 hover:text-pink-300 underline underline-offset-2 transition-colors duration-200"
        >
          🔗 BSCSAN
        </a>
      </p>
    </>
  ),
},

  {
    icon: <Lock className="w-5 h-5 text-yellow-400" />,
    title: 'Liquidity Wallet, Managed Weekly',
    summary: 'Liquidity stays locked, but we actively manage the wallet to keep trading smooth and the market healthy.',
    body: (
      <>
        <p>
          Each week, we review performance, adjust as needed, and send any excess back into the ecosystem — whether that’s burns, giveaways, or player rewards.
        </p>
        <ul className="list-disc pl-6 mt-2">
          <li>Locked liquidity to prevent rug pulls</li>
          <li>Weekly check-ins to keep markets balanced</li>
          <li>Transparent updates so you always know where things stand</li>
        </ul>
      </>
    ),
  },
  {
    icon: <Zap className="w-5 h-5 text-green-400" />,
    title: 'Smart Game Logic, Not Manual Rewards',
    summary: 'Reward tokens are sent automatically, not manually.',
    body: (
      <>
        <p>
          When you score, the game logic triggers the reward. No manual approvals. No waiting on a dev to send funds.
        </p>
        <ul className="list-disc pl-6 mt-2">
          <li>Fully on-chain transactions</li>
          <li>Game engine handles all triggers</li>
          <li>Everything logged and traceable</li>
        </ul>
      </>
    ),
  },
  {
    icon: <Eye className="w-5 h-5 text-blue-400" />,
    title: 'You Deserve Receipts',
    summary: 'We publish wallet addresses, logs, and rewards openly.',
    body: (
  <>
    <p>
      This project is built on verifiable trust — not vibes. That’s why we share our receipts.
    </p>
    <ul className="list-disc pl-6 mt-2">
      <li>Core wallet addresses listed publicly</li>
      <li>Token allocations disclosed up front</li>
      <li>10% of the total supply permanently burned</li>
      <li>50% of the loss supply locked as liquidity for stable trading</li>
      <li>Rewards logged both off-chain and on-chain</li>
    </ul>
  </>
),

  },
];

const TrustSection = () => {
  return (
    <section
  id="trust"
  className="pt-24 pb-40 px-4 md:px-12 bg-gradient-to-b from-[#40033e] to-[#c23278] text-white"
>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
        

        <Accordion.Root type="multiple" defaultValue={[]} className="space-y-6 max-w-4xl mx-auto">
          {trustItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15 }}
            >
              <Accordion.Item
  value={`item-${index}`}
  className="overflow-hidden rounded-2xl bg-[#3e0133] border border-[#70144e] shadow-xl"
>

                <Accordion.Header>
                  <Accordion.Trigger
  className="group w-full text-left p-6 flex items-start justify-between gap-4
             transition-colors
             focus:outline-none focus-visible:ring-0
             hover:bg-transparent"
>



                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">{item.icon}</div>
                      <div>
                        <h3 className="text-xl font-semibold">{item.title}</h3>
                        <p className="text-sm text-purple-300 mt-1">{item.summary}</p>
                      </div>
                    </div>
                    <span className="text-purple-400 text-xl group-data-[state=open]:hidden">+</span>
                    <span className="text-purple-400 text-xl hidden group-data-[state=open]:block">−</span>
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className="px-6 pb-6 text-purple-100 rounded-b-2xl bg-[#3e0133] mb-20">
                  {item.body}
                </Accordion.Content>
              </Accordion.Item>
            </motion.div>
          ))}
        </Accordion.Root>
      </motion.div>
    </section>
  );
};

export default TrustSection;
